import cn from 'classnames';
import { useCallback, useState } from 'preact/hooks';
import Button, { LinkButton } from '../button';
import type { ComponentChildren, TargetedEvent } from 'preact';
import type { HTMLAttributes } from '../../types/utility';
import type { IconType } from '../../types/icons';

interface BaseProps {
  action?: {
    label: string;
    onClick: (e: TargetedEvent<HTMLButtonElement, MouseEvent>) => void;
  };
  children?: ComponentChildren;
  icon?: IconType;
  theme?: 'default' | 'positive' | 'negative' | 'warning';
  type?: 'section' | 'page';
  onDismiss?: (e: TargetedEvent<HTMLButtonElement, MouseEvent>) => void;
}

type Props = BaseProps & Omit<HTMLAttributes<HTMLDivElement>, 'action'>;
type PreThemedProps = Omit<Props, 'theme'>;

export default function Banner({
  action,
  children,
  className,
  icon,
  theme = 'default',
  type = 'section',
  onDismiss,
  ...props
}: Props) {
  const classList = cn('banner', className, {
    'page-level': type === 'page',
    [`theme-${theme}`]: theme !== 'default',
  });

  return (
    <div
      className={classList}
      data-icon={icon ?? (theme === 'default' ? undefined : '')}
      {...props}
    >
      {children}
      {action && (
        <LinkButton className="action" onClick={action.onClick}>
          {action.label}
        </LinkButton>
      )}
      {onDismiss && (
        <Button
          aria-label="Dismiss"
          className="dismiss"
          icon="close"
          hierarchy="tertiary"
          onClick={onDismiss}
          isIconOnly
        />
      )}
    </div>
  );
}

interface UseBannerProps {
  isVisible?: boolean;
  onDismiss?: (e?: TargetedEvent<HTMLButtonElement, MouseEvent>) => void;
}

Banner.useBanner = function useBanner(props?: UseBannerProps) {
  const { onDismiss } = props ?? {};
  const [isVisible, setIsVisible] = useState(!!props?.isVisible);

  const handleDismiss = useCallback(
    (e?: TargetedEvent<HTMLButtonElement, MouseEvent>) => {
      setIsVisible(false);
      onDismiss?.(e);
    },
    [onDismiss],
  );

  return { isVisible, setIsVisible, handleDismiss };
};

export function SuccessBanner(props: PreThemedProps) {
  return <Banner {...props} theme="positive" icon="check" />;
}

export function ErrorBanner(props: PreThemedProps) {
  return <Banner {...props} theme="negative" icon="close" />;
}

export function WarningBanner(props: PreThemedProps) {
  return <Banner {...props} theme="warning" icon="error" />;
}
